import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from '@headlessui/react';
import { useVideoEditor } from "./context/VideoEditorContext";
import TimelineHeader from "./timeline/TimelineHeader";
import TimelineTracks from "./timeline/TimelineTracks";
import { getVideoDuration } from "../../utils/videoUtils";
import { useDialog } from "../../hooks/useDialog";
import { videoClipApi } from "../../services/user/videoclip.api";
import { useToast } from "../../stores/toastStore";
import { useGlobalLoading } from "../../stores/globalLoadingStore";
import { IVideoClip } from "@packages/global";
import SubtitleSettingsDialog from "./SubtitleSettingsDialog";
import BgmTab from "./tabs/BgmTab";

interface TimelineClip {
  id: string;
  name: string;
  startTime: number;
  duration: number;
  color: string;
  videoClip?: any;
  isGenerating?: boolean;
  hasAudio?: boolean;
  hasText?: boolean;
  editInfo?: any;
}

interface TimelineTrack {
  id: string;
  name: string;
  type: "video" | "audio" | "text" | "bgmusic";
  clips: TimelineClip[];
}

interface TimelineProps {
  onClipInserted?: (newClip: any, afterClipId: string) => void;
  onClipDeleted?: (clipId: string) => void;
  onClipCopied?: (newClip: any, afterClipId: string) => void;
  refreshEpisode?: () => Promise<void>;
}

export default function Timeline({
  onClipInserted,
  onClipDeleted,
  onClipCopied,
  refreshEpisode,
}: TimelineProps = {}) {
  const { state, actions } = useVideoEditor();
  const { confirm } = useDialog();
  const { success, error } = useToast();
  const { showLoading, hideLoading } = useGlobalLoading();
  const { t } = useTranslation(['editor', 'common']);
  const {
    isPlaying,
    currentTime,
    editConfig,
    videoClips,
    episode,
    selectedClip,
    selectedClipView,
    isBgmTrackActive,
    currentPlaybackInfo,
    pendingClipIds,
    resourceStatusMap,
  } = state;
  const { updateEditConfig } = actions;

  // 检查clip是否有视频相关资源在pending/generating
  const isVideoGenerating = (clipId: string, clip: IVideoClip): boolean => {
    // 如果已有视频或图片，不显示loading
    if (clip.videoUrl || clip.startImage) return false;

    const resources = resourceStatusMap.get(clipId) || [];
    const videoResourceTypes = [
      "start_image",
      "end_image",
      "video",
      "lip_sync_video",
    ];
    return resources.some(
      (r) =>
        videoResourceTypes.includes(r.resourceType) &&
        (r.status === "pending" || r.status === "processing"),
    );
  };

  // 检查clip是否有音频相关资源在pending/generating
  const isAudioGenerating = (clipId: string, clip: IVideoClip): boolean => {
    // 如果已有音频，不显示loading
    if (clip.scriptAudioUrl) return false;

    const resources = resourceStatusMap.get(clipId) || [];
    const audioResourceTypes = ["script_audio", "sound_effect_audio"];
    return resources.some(
      (r) =>
        audioResourceTypes.includes(r.resourceType) &&
        (r.status === "pending" || r.status === "processing"),
    );
  };

  const [zoom, setZoom] = useState(1.5);
  const [showBgmSettings, setShowBgmSettings] = useState(false);
  const [showSubtitleSettings, setShowSubtitleSettings] = useState(false);
  // 从 editConfig 中获取轨道启用状态
  const trackEnabled = {
    video: editConfig?.tracks?.video?.enabled ?? true,
    audio: editConfig?.tracks?.audio?.enabled ?? true,
    text: editConfig?.tracks?.subtitle?.enabled ?? true,
    bgmusic: editConfig?.tracks?.bgm?.enabled ?? true,
  };

  // 使用 editConfig 生成 timeline clips
  const videoTrackClips: TimelineClip[] = editConfig
    ? (editConfig.clips
      .map((clipInfo: any) => {
        const clip = videoClips.find((c) => c.id === clipInfo.clipId);
        if (!clip) return null;

        // 只有视频相关资源在生成中才显示loading
        const isGenerating = isVideoGenerating(clip.id, clip);
        return {
          id: clip.id,
          name: clip.description || clip.title,
          startTime: clipInfo.startTime,
          duration: clipInfo.duration,
          color: isGenerating
            ? "bg-orange-500 animate-pulse"
            : "bg-purple-500",
          videoClip: clip,
          isGenerating,
          editInfo: clipInfo,
        };
      })
      .filter(Boolean) as TimelineClip[])
    : [];

  // Create audio track clips
  const audioTrackClips: TimelineClip[] = [];
  videoTrackClips.forEach((timelineClip) => {
    const clip = timelineClip.videoClip;
    if (!clip) return;

    // 只有音频相关资源在生成中才显示loading
    const isGenerating = isAudioGenerating(clip.id, clip);
    // 为每个视频片段都创建音频轨道项，即使没有音频内容
    audioTrackClips.push({
      id: `audio-${clip.id}`,
      name: clip.script || clip.title,
      startTime: timelineClip.startTime,
      duration: timelineClip.duration,
      color: isGenerating ? "bg-orange-500 animate-pulse" : "bg-blue-500/50",
      videoClip: clip, // 始终保持对原始clip的引用
      isGenerating,
      editInfo: timelineClip.editInfo,
      hasAudio: !!(
        clip.scriptAudioUrl ||
        clip.soundEffectAudioUrl
      ), // 标记是否有音频
    });
  });

  // Create subtitle track clips
  const subtitleTrackClips: TimelineClip[] = [];
  videoTrackClips.forEach((timelineClip) => {
    const clip = timelineClip.videoClip;
    if (!clip) return;

    // 为每个视频片段都创建字幕轨道项，即使没有字幕内容
    subtitleTrackClips.push({
      id: `subtitle-${clip.id}`,
      name: clip.script || clip.title,
      startTime: timelineClip.startTime,
      duration: timelineClip.duration,
      color: "bg-yellow-500",
      videoClip: clip, // 始终保持对原始clip的引用
      hasText: !!clip.script, // 标记是否有字幕内容
    });
  });

  const totalDuration =
    videoTrackClips.length > 0
      ? Math.max(
        ...videoTrackClips.map((clip) => clip.startTime + clip.duration),
      )
      : 0;

  // Create background music track clips
  const bgmTrackClips: TimelineClip[] = [];
  if (totalDuration > 0) {
    bgmTrackClips.push({
      id: "bgm-track",
      name: episode?.bgmUrl ? t('editor:track.bgm') : t('editor:bgm.noSelection'),
      startTime: 0,
      duration: totalDuration,
      color: "bg-gray-500 opacity-50",
    });
  }

  const tracks: TimelineTrack[] = [
    { id: "1", name: t('editor:track.video'), type: "video", clips: videoTrackClips },
    { id: "2", name: t('editor:track.audio'), type: "audio", clips: audioTrackClips },
    { id: "3", name: t('editor:track.subtitle'), type: "text", clips: subtitleTrackClips },
    { id: "4", name: t('editor:track.bgm'), type: "bgmusic", clips: bgmTrackClips },
  ];

  const pixelsPerSecond = 20 * zoom;

  const handleZoomIn = () => setZoom((prev) => Math.min(prev * 1.5, 5));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev / 1.5, 0.2));

  const handleTrackToggle = (trackType: "text") => {
    if (!editConfig) return;

    const newEnabled = !trackEnabled[trackType];

    // 更新 editConfig 中的轨道状态
    const updatedTracks = { ...editConfig.tracks };

    if (trackType === "text") {
      updatedTracks.subtitle = {
        ...updatedTracks.subtitle,
        enabled: newEnabled,
      };
      // 通知 Context
      actions.toggleSubtitle();
    }

    // 保存到 editConfig
    const newEditConfig = {
      ...editConfig,
      tracks: updatedTracks,
    };

    updateEditConfig(newEditConfig);
  };

  const handleBgmVolumeChange = (volume: number) => {
    if (!editConfig) return;

    const updatedTracks = {
      ...editConfig.tracks,
      bgm: {
        enabled: volume > 0,
        volume,
      },
    };

    const newEditConfig = {
      ...editConfig,
      tracks: updatedTracks,
    };

    updateEditConfig(newEditConfig);
  };

  const handleVideoVolumeChange = (volume: number) => {
    if (!editConfig) return;

    const updatedTracks = {
      ...editConfig.tracks,
      video: {
        enabled: volume > 0,
        volume,
      },
    };

    const newEditConfig = {
      ...editConfig,
      tracks: updatedTracks,
    };

    updateEditConfig(newEditConfig);
  };

  const handleAudioVolumeChange = (volume: number) => {
    if (!editConfig) return;

    const updatedTracks = {
      ...editConfig.tracks,
      audio: {
        enabled: volume > 0,
        volume,
      },
    };

    const newEditConfig = {
      ...editConfig,
      tracks: updatedTracks,
    };

    updateEditConfig(newEditConfig);
  };

  const hasAudioClips = audioTrackClips.some((clip) => clip.hasAudio && !clip.isGenerating);
  const videoClipsMuted = videoTrackClips.length > 0
    ? videoTrackClips.every((clip) => (clip.editInfo?.videoVolume ?? 0) === 0)
    : true;
  const audioClipsMuted = hasAudioClips
    ? audioTrackClips
      .filter((clip) => clip.hasAudio && !clip.isGenerating)
      .every((clip) => (clip.editInfo?.dialogueVolume ?? 1) === 0)
    : true;

  const handleClipSelect = (
    clip: any,
    trackType: "video" | "audio" | "text" | "bgmusic",
  ) => {
    // 背景音乐轨道：切换激活状态，不改变选中的clip
    if (trackType === "bgmusic") {
      actions.toggleBgmTrack();
      return;
    }

    // 停止播放
    if (isPlaying) {
      actions.pause();
    }

    // 定位到clip开头
    actions.seek(clip.startTime);

    // 对于 video/audio/text 轨道，选中 clip 并设置对应视图
    // 注意：直接调用 selectClip，不依赖 isPlaying 状态（pause 是异步的）
    if (clip.videoClip) {
      actions.selectClip(
        clip.videoClip,
        trackType as "video" | "audio" | "text",
      );
    }
  };

  const handleCopyClip = async (clipId: string) => {
    if (!episode?.id || !episode?.projectId) return;

    try {
      // 确认对话框
      const confirmed = await confirm(
        t('editor:clip.copyConfirm'),
        t('editor:clip.copy'),
        "warning",
      );

      if (!confirmed) return;

      showLoading(t('editor:common.copying'));

      // 找到要复制的 clip
      const sourceClip = videoClips.find((c) => c.id === clipId);
      if (!sourceClip) return;

      // 生成新 clip 的数据（复制原 clip 的属性）
      const newSequence = sourceClip.sequence + 1;
      const newClipData = {
        title: sourceClip.title + " - 副本",
        description: sourceClip.description,
        sequence: newSequence,
        script: sourceClip.script,
        speaker: sourceClip.speaker,
        sceneName: sourceClip.sceneName,
        structure: sourceClip.structure,
        cameraWork: sourceClip.cameraWork,
        transition: sourceClip.transition,
        emotion: sourceClip.emotion,
        scriptAudioUrl: sourceClip.scriptAudioUrl,
        soundEffectAudioUrl: sourceClip.soundEffectAudioUrl,
        startImage: sourceClip.startImage,
        endImage: sourceClip.endImage,
        duration: sourceClip.duration,
        videoUrl: sourceClip.videoUrl,
        episodeId: episode.id,
        projectId: episode.projectId,
      };

      // 调用 API 创建新 clip
      const newClip = await videoClipApi.createVideoClip(newClipData);

      // 更新后续 clips 的 sequence
      const clipsToUpdate = videoClips.filter(
        (c) => c.sequence >= newSequence && c.id !== clipId,
      );
      for (const clip of clipsToUpdate) {
        await videoClipApi.updateVideoClip(clip.id, {
          sequence: clip.sequence + 1,
        });
      }

      success(t('editor:common.copySuccess'));

      // 通知父组件复制成功，传递新 clip 和源 clip ID
      if (onClipCopied) {
        onClipCopied(newClip, clipId);
      }
    } catch (err) {
      console.error("Failed to copy clip:", err);
      error(t('editor:common.copyFailed'));
    } finally {
      hideLoading();
    }
  };
  const handleDeleteClip = async (clipId: string) => {
    if (!editConfig) return;

    try {
      // 确认对话框
      const confirmed = await confirm(
        t('editor:clip.deleteConfirm'),
        t('editor:clip.delete'),
        "danger",
      );

      if (!confirmed) return;

      showLoading(t('editor:common.deleting'));

      // 从 editConfig 中移除 clip
      const clipIndex = editConfig.clips.findIndex((c) => c.clipId === clipId);
      if (clipIndex === -1) return;

      const clipToDelete = editConfig.clips[clipIndex];
      const updatedClips = [...editConfig.clips];

      // 移除目标 clip
      updatedClips.splice(clipIndex, 1);

      // 更新后续 clips 的 startTime
      for (let i = clipIndex; i < updatedClips.length; i++) {
        updatedClips[i].startTime -= clipToDelete.duration;
      }

      // 更新 editConfig
      const newEditConfig = {
        ...editConfig,
        timeline: {
          ...editConfig.timeline,
          totalDuration:
            editConfig.timeline.totalDuration - clipToDelete.duration,
        },
        clips: updatedClips,
      };

      updateEditConfig(newEditConfig);

      // 通知父组件删除成功
      if (onClipDeleted) {
        onClipDeleted(clipId);
      }

      success(t('editor:common.deleteSuccess'));
    } catch (err) {
      console.error("Failed to delete clip:", err);
      error(t('editor:common.deleteFailed2'));
    } finally {
      hideLoading();
    }
  };
  const handleInsertClip = async (afterClipId: string) => {
    if (!episode?.id || !episode?.projectId) return;

    try {
      // 确认对话框
      const confirmed = await confirm(
        t('editor:clip.insertConfirm'),
        t('editor:clip.insert'),
        "warning",
      );

      if (!confirmed) return;

      showLoading(t('editor:common.inserting'));

      // 找到要插入位置的 clip
      const afterClip = videoClips.find((c) => c.id === afterClipId);
      if (!afterClip) return;

      // 生成新 clip 的默认数据
      const newSequence = afterClip.sequence + 1;
      const newClipData = {
        title: t('editor:clip.newTitle', { seq: newSequence }),
        description: `New clip ${newSequence}`,
        sequence: newSequence,
        script: "",
        episodeId: episode.id,
        projectId: episode.projectId,
      };

      // 调用 API 创建新 clip
      const newClip = await videoClipApi.createVideoClip(newClipData);

      // 更新后续 clips 的 sequence
      const clipsToUpdate = videoClips.filter(
        (c) => c.sequence >= newSequence && c.id !== afterClipId,
      );
      for (const clip of clipsToUpdate) {
        await videoClipApi.updateVideoClip(clip.id, {
          sequence: clip.sequence + 1,
        });
      }

      success(t('editor:common.insertSuccess'));

      // 无感更新：通知父组件刷新数据并更新 editConfig
      if (onClipInserted) {
        onClipInserted(newClip, afterClipId);
      }
    } catch (err) {
      console.error("Failed to insert clip:", err);
      error(t('editor:common.insertFailed'));
    } finally {
      hideLoading();
    }
  };

  const handleToggleVideoVolume = (clipId: string, volume: number) => {
    if (!editConfig) return;

    const updatedClips = editConfig.clips.map((clipInfo) => {
      if (clipInfo.clipId === clipId) {
        return { ...clipInfo, videoVolume: volume };
      }
      return clipInfo;
    });

    const newEditConfig = {
      ...editConfig,
      clips: updatedClips,
    };

    updateEditConfig(newEditConfig);
  };

  const handleToggleAudioVolume = (clipId: string, volume: number) => {
    if (!editConfig) return;

    const updatedClips = editConfig.clips.map((clipInfo) => {
      if (clipInfo.clipId === clipId) {
        return { ...clipInfo, dialogueVolume: volume };
      }
      return clipInfo;
    });

    const newEditConfig = {
      ...editConfig,
      clips: updatedClips,
    };

    updateEditConfig(newEditConfig);
  };

  const handleToggleAllVideoVolume = () => {
    if (!editConfig) return;

    const nextVolume = videoClipsMuted ? 1 : 0;
    const updatedClips = editConfig.clips.map((clipInfo) => ({
      ...clipInfo,
      videoVolume: nextVolume,
    }));

    updateEditConfig({
      ...editConfig,
      clips: updatedClips,
    });
  };

  const handleToggleAllAudioVolume = () => {
    if (!editConfig || !hasAudioClips) return;

    const nextVolume = audioClipsMuted ? 1 : 0;
    const audioClipIds = new Set(
      audioTrackClips
        .filter((clip) => clip.hasAudio && !clip.isGenerating)
        .map((clip) => clip.videoClip?.id)
        .filter(Boolean),
    );
    const updatedClips = editConfig.clips.map((clipInfo) => (
      audioClipIds.has(clipInfo.clipId)
        ? { ...clipInfo, dialogueVolume: nextVolume }
        : clipInfo
    ));

    updateEditConfig({
      ...editConfig,
      clips: updatedClips,
    });
  };

  return (
    <div
      className="flex flex-col h-full bg-gray-800 overflow-hidden"
      style={{ maxWidth: "100%" }}
    >
      <TimelineHeader
        videoClipsCount={videoClips.length}
        isPlaying={isPlaying}
        currentTime={currentTime}
        totalDuration={totalDuration}
        zoom={zoom}
        currentPlayingClip={currentPlaybackInfo?.clip || null}
        trackEnabled={trackEnabled}
        onTogglePlay={actions.togglePlay}
        onSeek={actions.seek}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />

      <div className="flex flex-1 overflow-hidden" style={{ minWidth: 0 }}>
        <TimelineTracks
          tracks={tracks}
          trackEnabled={trackEnabled}
          videoVolume={editConfig?.tracks?.video?.volume ?? 1}
          audioVolume={editConfig?.tracks?.audio?.volume ?? 0.8}
          bgmVolume={editConfig?.tracks?.bgm?.volume ?? 0.3}
          videoClipsMuted={videoClipsMuted}
          audioClipsMuted={audioClipsMuted}
          hasAudioClips={hasAudioClips}
          selectedClip={selectedClip?.id || null}
          selectedTrackType={selectedClipView}
          isBgmTrackActive={isBgmTrackActive}
          currentTime={currentTime}
          pixelsPerSecond={pixelsPerSecond}
          totalDuration={totalDuration}
          onTrackToggle={handleTrackToggle}
          onSubtitleSettingsOpen={() => setShowSubtitleSettings(true)}
          onClipSelect={handleClipSelect}
          onInsertClip={handleInsertClip}
          onDeleteClip={handleDeleteClip}
          onCopyClip={handleCopyClip}
          onBgmSelect={() => setShowBgmSettings(true)}
          onVideoVolumeChange={handleVideoVolumeChange}
          onAudioVolumeChange={handleAudioVolumeChange}
          onBgmVolumeChange={handleBgmVolumeChange}
          onToggleVideoVolume={handleToggleVideoVolume}
          onToggleAudioVolume={handleToggleAudioVolume}
          onToggleAllVideoVolume={handleToggleAllVideoVolume}
          onToggleAllAudioVolume={handleToggleAllAudioVolume}
        />
      </div>

      <Transition appear show={showBgmSettings && !!episode} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowBgmSettings(false)}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-bg-surface border border-border-subtle shadow-2xl">
                <BgmTab
                  episode={episode!}
                  refreshEpisode={refreshEpisode}
                  onRequestClose={() => setShowBgmSettings(false)}
                />
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition>

      <SubtitleSettingsDialog
        episodeId={episode?.id}
        editConfig={editConfig}
        isOpen={showSubtitleSettings}
        onClose={() => setShowSubtitleSettings(false)}
        onChange={updateEditConfig}
      />
    </div>
  );
}
